// src/services/repsService.ts
//
// All Supabase access for the yak_reps table (rep roster) is isolated here.
// Roster import is admin config: direct table writes under RLS (spine AD-3)
// — the SELECT policy covers all org members, the write policies require an
// admin role, and the DB triggers enforce phone/leaf integrity. The CSV is
// parsed and pre-validated by src/utils/repImport.ts; this service upserts
// the candidates sequentially and turns any DB-side rejection into report
// copy. Components/hooks consume the clean src/types/yak.ts types only.

import { supabase } from '../lib/supabase'
import type { YakRep } from '../types/yak'
import {
  isPlausibleEmail,
  normalizePhoneUs,
  type CandidateRep,
  type ImportReport,
  type ImportRowRejection,
} from '../utils/repImport'

// Fields an admin can correct on an existing rep (Story 1.3). `active` is
// changed only through setRepActive — a re-save of the edit form must never
// silently flip a rep's active state.
export interface RepEditFields {
  name: string
  email: string
  phones: string[]
  region_id: string
  // The rep's region_id BEFORE this edit. The leaf guard is a
  // `BEFORE UPDATE OF region_id` trigger that fires whenever region_id is in
  // the UPDATE's SET list — even when the value is unchanged. A rep whose
  // once-leaf region has since gained children would then have every
  // unrelated edit (name/phone/email) rejected for a region they never
  // touched. So we write region_id only when it actually changes. When this
  // is omitted (undefined), region_id is always written (back-compat).
  currentRegionId?: string
}

interface PostgrestErrorLike {
  message: string
  code?: string
}

// Raw Postgres constraint text must never reach the import report. Our phone
// and leaf triggers raise human-authored messages; the table CHECK
// constraints share code 23514 but emit raw text — this pattern catches
// those so they fall through to generic copy.
const RAW_PG_MESSAGE =
  /violates (check|not-null|foreign key|unique) constraint|null value in column/i

// Map a per-row upsert error to authored report copy. Exported + unit-tested
// because it is the concurrency-safety contract (the throwMapped lesson from
// Story 1.1): the codes here are reachable when the roster changes under a
// running import, and the raw Postgres string must never leak.
export function mapImportError(error: PostgrestErrorLike): string {
  // 23P01 = the duplicate-active-phone guard; 23514 = the leaf guard or the
  // phone-normalize failure. Both raise messages we wrote — surface them
  // verbatim, unless the message is raw constraint text (a CHECK the client
  // validator should have caught first).
  if (
    (error.code === '23P01' || error.code === '23514') &&
    error.message &&
    !RAW_PG_MESSAGE.test(error.message)
  ) {
    return error.message
  }
  // The composite region FK: the named region was deleted mid-import.
  if (error.code === '23503') {
    return 'region no longer exists — refresh and re-upload'
  }
  // Another writer upserted this rep between our read and write.
  if (error.code === '23505') {
    return 'another import updated this rep at the same time — re-upload'
  }
  return 'could not be imported — please try again'
}

// Map a per-rep edit / activation error to friendly, retryable inline copy —
// the edit-surface twin of mapImportError, and the same concurrency-safety
// contract as regionsService.throwMapped (Story 1.1): every code reachable
// under a concurrent roster change maps to authored copy, and raw Postgres
// text never reaches the admin. Exported + unit-tested. The phone guard
// (23P01) and phone/leaf guards (23514) already raise human-authored
// messages naming the offending value — surfaced verbatim so this screen
// agrees with the import report — unless the string is raw constraint text
// (a table CHECK the service should have caught first), which is suppressed.
export function mapRepWriteError(
  error: PostgrestErrorLike,
  action: 'edit' | 'reactivate' = 'edit'
): string {
  if (
    (error.code === '23P01' || error.code === '23514') &&
    error.message &&
    !RAW_PG_MESSAGE.test(error.message)
  ) {
    // On reactivation the guard trips because a departed rep's number was
    // claimed by another active rep while they were inactive (AC 4). Tell
    // the admin the fix, keeping the specific number the trigger named.
    if (action === 'reactivate' && error.code === '23P01') {
      return `Can't reactivate — ${error.message}. Free that number from the other rep (or edit this rep's phones) first, then reactivate.`
    }
    return error.message
  }
  // Duplicate email: distinct from the phone guard by code (23505 vs 23P01),
  // so no constraint-name disambiguation is needed.
  if (error.code === '23505') {
    return 'A rep with this email already exists in your organization'
  }
  // The composite region FK: the picked region vanished mid-edit.
  if (error.code === '23503') {
    return 'That region no longer exists — refresh and try again'
  }
  // 0-row update: another admin changed or removed this rep first.
  if (error.code === 'PGRST116') {
    return 'This rep no longer exists — it may have been changed by someone else. Refresh and try again'
  }
  return 'That change could not be saved — please try again'
}

class RepsService {
  // Whole org roster as flat rows (RLS scopes to the caller's org), ordered
  // by name so the table renders stably at pilot scale.
  async listReps(): Promise<YakRep[]> {
    const { data, error } = await supabase
      .from('yak_reps')
      .select('*')
      .order('name', { ascending: true })

    if (error) throw new Error(error.message)
    return (data as unknown as YakRep[] | null) || []
  }

  // Sequential per-row upsert keyed on (organization_id, email): existing
  // email updates name/phones/region, a new email inserts born active. A
  // failed row is recorded and the loop continues — partial import is the
  // designed behavior. `active` is deliberately omitted from the payload so
  // new rows take the DB default and existing rows keep their current state
  // (a re-upload must never silently reactivate a deactivated rep).
  async importReps(candidates: CandidateRep[]): Promise<ImportReport> {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    const organizationId = session?.user?.app_metadata?.organization_id ?? null
    if (!organizationId) {
      throw new Error('Your account is not linked to an organization')
    }

    const rejections: ImportRowRejection[] = []
    let accepted = 0

    for (const candidate of candidates) {
      const { error } = await supabase.from('yak_reps').upsert(
        {
          organization_id: organizationId,
          name: candidate.name,
          email: candidate.email,
          phones: candidate.phones,
          region_id: candidate.region_id,
        },
        { onConflict: 'organization_id,email' }
      )

      if (error) {
        rejections.push({
          line: candidate.line,
          email: candidate.email,
          reason: mapImportError(error),
        })
      } else {
        accepted++
      }
    }

    return { accepted, rejected: rejections.length, rejections }
  }

  // Correct one rep's details (Story 1.3). Admin config = a single direct
  // UPDATE under RLS (spine AD-3) — phones are a text[] column so the whole
  // edit is atomic. Values are trimmed/normalized at the boundary so no
  // caller can persist junk, but the DB triggers/CHECKs remain authoritative:
  // the duplicate-active-phone guard, leaf guard, and email uniqueness fire
  // here exactly as on import. `active` is deliberately not in the payload.
  async updateRep(id: string, fields: RepEditFields): Promise<YakRep> {
    const name = fields.name.trim()
    if (!name) throw new Error('Rep name cannot be empty')

    const email = fields.email.trim().toLowerCase()
    if (!email) throw new Error('Rep email cannot be empty')
    if (!isPlausibleEmail(email)) {
      throw new Error(`“${fields.email.trim()}” is not a valid email address`)
    }

    // Normalize through the same helper the import uses; a second normalizer
    // that disagreed would let duplicates slip past the guard or falsely
    // reject valid edits. Dedupe within the row (the DB does this too).
    const phones: string[] = []
    for (const raw of fields.phones) {
      const norm = normalizePhoneUs(raw)
      if (norm === null) {
        throw new Error(`“${raw}” is not a valid US phone number`)
      }
      if (!phones.includes(norm)) phones.push(norm)
    }
    if (phones.length === 0) {
      throw new Error('A rep needs at least one phone number')
    }

    // Only include region_id in the SET list when it actually changes — the
    // leaf guard fires on column presence, not on a value change (see
    // RepEditFields.currentRegionId). Re-writing an unchanged, now-interior
    // region would wrongly reject an unrelated name/phone/email edit.
    const patch: Record<string, unknown> = { name, email, phones }
    if (
      fields.currentRegionId === undefined ||
      fields.region_id !== fields.currentRegionId
    ) {
      patch.region_id = fields.region_id
    }

    const { data, error } = await supabase
      .from('yak_reps')
      .update(patch)
      .eq('id', id)
      .select('*')
      .single()

    if (error) throw new Error(mapRepWriteError(error, 'edit'))
    return data as unknown as YakRep
  }

  // Deactivate (active=false) or reactivate (active=true) a rep. There is no
  // delete path for reps anywhere — history is retained (FR-1); this flag is
  // the only lifecycle. Reactivation re-trips the duplicate-active-phone
  // guard (AC 4): an inactive rep's numbers are outside the guard, so turning
  // them active again re-checks them against the current active roster.
  async setRepActive(id: string, active: boolean): Promise<YakRep> {
    const { data, error } = await supabase
      .from('yak_reps')
      .update({ active })
      .eq('id', id)
      .select('*')
      .single()

    if (error) {
      throw new Error(mapRepWriteError(error, active ? 'reactivate' : 'edit'))
    }
    return data as unknown as YakRep
  }
}

export const repsService = new RepsService()
